import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Snackbar,
  Switch,
  Tooltip,
  Typography
} from '@mui/material';
import ViewCarouselOutlinedIcon from '@mui/icons-material/ViewCarouselOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import AddIcon from '@mui/icons-material/Add';
import {
  deleteProductModule,
  getAboutTabs,
  getCustomPages,
  getProductModules,
  saveAboutTab,
  saveCustomPage,
  saveProductModule
} from '../../../services/ecommerceApi';
import type { CustomModule } from '../ECommercePage';
import ContentCardsDialog from './contentCards/ContentCardsDialog';
import ContentCardsStylePreview from './contentCards/ContentCardsStylePreview';
import { normalizeContentCardVariant } from './contentCards/contentCardStyleOptions';
import { getContentCardTemplate, getContentCardTemplateLabel } from './contentCards/contentCardTemplates';
import { getContentCardLayoutLabel, normalizeContentCardsLayout } from './contentCards/contentCardLayoutTemplates';
import {
  cleanContentCardsPayload,
  normalizeContentCardForEditor,
  parseContentCardsFromHtml,
  stripContentCardsFromHtml
} from './contentCards/contentCardsUtils';
import {
  BANNER_PANEL_COLORS,
  DEFAULT_CONTENT_CARD,
  DEFAULT_CONTENT_CARDS_DIALOG,
  DEFAULT_CONTENT_CARDS_SETTINGS,
  type ContentCardsDialogState,
  type ContentCardsInsertAfter,
  type ContentCardsPageOption,
  type ContentCardsPayload,
  type ContentCardsTargetPage,
  type ContentCardVariant
} from './contentCards/types';
import ImageLibrarySelectionModal from '../../accounting/inventory/ImageLibrarySelectionModal';

interface HomePageContentCardsPanelProps {
  companyId: string;
  configId: string;
  homepageModules?: CustomModule[];
  /** promo: Pazarlama > Kart Oluştur — sade banner şablonları */
  mode?: 'default' | 'promo';
}

interface StoredContentCards extends ContentCardsPayload {
  enabled: boolean;
}

interface ContentCardsBlockRecord {
  moduleId: string;
  targetPage: ContentCardsTargetPage;
  displayOrder: number;
  enabled: boolean;
  payload: StoredContentCards;
}

interface SiblingPageBlock {
  moduleId: string;
  targetPage: string;
  title: string;
  kind: 'cards' | 'tags';
}

/** Sekmeli sayfa tipleri: admin hedef seçeneği + API pageType eşlemesi */
const TAB_TARGET_CONFIGS = [
  { prefix: 'about-tab', pageType: 'AboutTab', group: 'Hakkımızda' },
  { prefix: 'services-tab', pageType: 'ServiceTab', group: 'Hizmetler' },
  { prefix: 'product-solution-tab', pageType: 'ProductSolutionTab', group: 'Ürün ve Çözümler' },
  { prefix: 'reference-tab', pageType: 'ReferenceTab', group: 'Referanslar' }
] as const;

const HOMEPAGE_OPTION: ContentCardsPageOption = { value: 'homepage', label: 'Ana Sayfa', group: 'Ana Sayfa' };

const parseStoredContentCards = (settingsJson?: string | null): StoredContentCards | null => {
  if (!settingsJson) return null;
  try {
    const parsed = JSON.parse(settingsJson);
    const payload = cleanContentCardsPayload({
      title: parsed?.title,
      cards: parsed?.cards || [],
      settings: parsed?.settings || DEFAULT_CONTENT_CARDS_SETTINGS,
      position: parsed?.position
    });
    return {
      ...payload,
      enabled: parsed?.enabled !== false
    };
  } catch {
    return null;
  }
};

const tabLabel = (tab: any): string => String(tab?.title || tab?.name || 'İsimsiz Sekme');

/** Soft-delete / gizli sekmeler listeye girmez */
const isTabActive = (tab: any): boolean => tab?.isActive !== false && tab?.IsActive !== false;

/** Yalnızca aktif ana sekmeler ve onların aktif alt sekmeleri (yetim/silinmiş parent altları hariç) */
const buildTabOptions = (tabs: any[], prefix: string, group: string): ContentCardsPageOption[] => {
  const sorted = [...tabs]
    .filter(isTabActive)
    .sort((a, b) => (Number(a?.displayOrder) || 0) - (Number(b?.displayOrder) || 0));
  const mains = sorted.filter((tab) => !tab?.parentId);
  const mainIds = new Set(mains.map((main) => String(main.id)));
  const subsByParent = new Map<string, any[]>();
  sorted
    .filter((tab) => tab?.parentId && mainIds.has(String(tab.parentId)))
    .forEach((tab) => {
      const list = subsByParent.get(String(tab.parentId)) || [];
      list.push(tab);
      subsByParent.set(String(tab.parentId), list);
    });

  const options: ContentCardsPageOption[] = [];
  mains.forEach((main) => {
    options.push({
      value: `${prefix}:${main.id}` as ContentCardsTargetPage,
      label: tabLabel(main),
      group
    });
    (subsByParent.get(String(main.id)) || []).forEach((sub) => {
      options.push({
        value: `${prefix}:${sub.id}` as ContentCardsTargetPage,
        label: `${tabLabel(main)} › ${tabLabel(sub)}`,
        group
      });
    });
  });

  return options;
};

const HomePageContentCardsPanel: React.FC<HomePageContentCardsPanelProps> = ({
  companyId,
  configId,
  homepageModules = [],
  mode = 'default'
}) => {
  const isPromoMode = mode === 'promo';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [storedBlocks, setStoredBlocks] = useState<ContentCardsBlockRecord[]>([]);
  const [siblingTagBlocks, setSiblingTagBlocks] = useState<SiblingPageBlock[]>([]);
  const [enabled, setEnabled] = useState(false);
  const [editingModuleId, setEditingModuleId] = useState<string | null>(null);
  const [pageTargetOptions, setPageTargetOptions] = useState<ContentCardsPageOption[]>([HOMEPAGE_OPTION]);
  const [dialogState, setDialogState] = useState<ContentCardsDialogState>(DEFAULT_CONTENT_CARDS_DIALOG);
  const [selectedTemplate, setSelectedTemplate] = useState<ContentCardVariant>(
    isPromoMode ? 'banner' : 'minimal'
  );
  const [imageLibraryPicker, setImageLibraryPicker] = useState<{
    open: boolean;
    targetIndex: number;
    field: 'imageUrl' | 'brandLogoUrl';
  }>({
    open: false,
    targetIndex: -1,
    field: 'imageUrl'
  });
  const [snack, setSnack] = useState<{ open: boolean; type: 'success' | 'error'; message: string }>({
    open: false,
    type: 'success',
    message: ''
  });
  const legacyMigrationDoneRef = useRef(false);

  const positionOptions = useMemo(() => {
    const moduleOptions = homepageModules
      .filter((module) => module.tabName !== 'İçerik Kartları')
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((module) => ({
        value: module.id,
        label: `${module.tabName} modülünden sonra`
      }));

    return [
      { value: 'categories', label: 'Kategorilerden sonra' },
      ...moduleOptions,
      { value: 'end', label: 'Tüm modüllerden sonra (en alta)' }
    ];
  }, [homepageModules]);

  const pagePositionOptions = useMemo(() => {
    const targetPage = dialogState.targetPage || 'homepage';
    if (targetPage === 'homepage') return [];

    const options: Array<{ value: string; label: string }> = [
      { value: 'content-start', label: 'Sayfa içeriğinin üstünde' },
      { value: 'content-end', label: 'Sayfa içeriğinin altında' }
    ];

    storedBlocks
      .filter(
        (block) =>
          block.targetPage === targetPage &&
          block.moduleId !== editingModuleId &&
          block.enabled
      )
      .forEach((block) => {
        const title = block.payload.title || `Kart yapısı (${block.payload.cards.length} kart)`;
        options.push({
          value: block.moduleId,
          label: `"${title}" kart yapısının altında`
        });
      });

    siblingTagBlocks
      .filter((block) => block.targetPage === targetPage)
      .forEach((block) => {
        options.push({
          value: block.moduleId,
          label: `"${block.title}" etiketlerinin altında`
        });
      });

    return options;
  }, [dialogState.targetPage, storedBlocks, siblingTagBlocks, editingModuleId]);

  const resolveHomepageDisplayOrder = (nextInsertAfter: ContentCardsInsertAfter): number => {
    const sortedModules = [...homepageModules]
      .filter((module) => module.tabName !== 'İçerik Kartları')
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    if (nextInsertAfter === 'categories') {
      const firstOrder = sortedModules[0]?.order ?? 0;
      return Math.max(0, firstOrder - 1);
    }

    if (nextInsertAfter === 'end') {
      const lastOrder = sortedModules[sortedModules.length - 1]?.order ?? 0;
      return lastOrder + 1;
    }

    const anchorIndex = sortedModules.findIndex((module) => module.id === nextInsertAfter);
    if (anchorIndex < 0) return 0;

    const anchorOrder = sortedModules[anchorIndex]?.order ?? anchorIndex;
    const nextOrder = sortedModules[anchorIndex + 1]?.order;
    if (typeof nextOrder === 'number') {
      return anchorOrder + (nextOrder - anchorOrder) / 2;
    }
    return anchorOrder + 1;
  };

  const resolveNextHomepageOrderForAnchor = (
    blocks: ContentCardsBlockRecord[],
    anchor: ContentCardsInsertAfter
  ): number => {
    const sameAnchorBlocks = blocks.filter(
      (block) =>
        block.targetPage === 'homepage' && (block.payload.position?.insertAfter || 'categories') === anchor
    );

    if (sameAnchorBlocks.length === 0) {
      return resolveHomepageDisplayOrder(anchor);
    }

    return Math.max(...sameAnchorBlocks.map((block) => block.displayOrder ?? 0)) + 1;
  };

  const resolveNextOrderForTarget = (
    blocks: ContentCardsBlockRecord[],
    targetPage: ContentCardsTargetPage
  ): number => {
    const sameTarget = blocks.filter((block) => block.targetPage === targetPage);
    if (sameTarget.length === 0) return 0;
    return Math.max(...sameTarget.map((block) => block.displayOrder ?? 0)) + 1;
  };

  const fetchModuleBlocks = async (): Promise<ContentCardsBlockRecord[]> => {
    const response = await getProductModules(companyId, 'content-cards');
    const rows = Array.isArray(response?.data) ? response.data : [];
    const blocks = rows
      .map((row: any) => {
        const parsed = parseStoredContentCards(row?.settingsJson);
        if (!parsed) return null;
        return {
          moduleId: String(row.id),
          targetPage: parsed.position?.targetPage || 'homepage',
          displayOrder: Number(row.displayOrder) || 0,
          enabled: row.isActive !== false && parsed.enabled !== false,
          payload: parsed
        } satisfies ContentCardsBlockRecord;
      })
      .filter(Boolean) as ContentCardsBlockRecord[];

    blocks.sort((a, b) => a.displayOrder - b.displayOrder);
    return blocks;
  };

  const persistBlockModule = async (
    payload: ContentCardsPayload,
    options: {
      moduleId?: string | null;
      targetPage: ContentCardsTargetPage;
      insertAfter: ContentCardsInsertAfter;
      displayOrder: number;
      isActive: boolean;
    }
  ) => {
    const settingsJson = JSON.stringify({
      enabled: options.isActive,
      title: payload.title,
      cards: payload.cards,
      settings: payload.settings,
      position: {
        insertAfter: options.insertAfter,
        targetPage: options.targetPage
      }
    });

    return saveProductModule({
      id: options.moduleId || undefined,
      companyId,
      configId,
      moduleType: 'content-cards',
      tabName: 'İçerik Kartları',
      sectionTitle: payload.title || 'İçerik Kartları',
      displayOrder: options.displayOrder,
      isActive: options.isActive,
      settingsJson,
      selectedProductIds: JSON.stringify([]),
      selectedCategoryIds: JSON.stringify([]),
      maxCategories: 0,
      horizontalPosition: 50,
      itemsPerRow: 3
    });
  };

  /**
   * Eski model: kartlar sayfa HTML'inin içine gömülüyordu. Panelde listelenip
   * yönetilebilmeleri için gömülü blokları modüle taşıyıp HTML'den temizler.
   */
  const migrateLegacyEmbeddedBlocks = async (
    tabsByConfig: Array<{ prefix: string; pageType: string; tabs: any[] }>,
    customPages: any[],
    existingBlocks: ContentCardsBlockRecord[]
  ): Promise<boolean> => {
    let migrated = false;
    let nextOrderBase = existingBlocks.length > 0
      ? Math.max(...existingBlocks.map((block) => block.displayOrder ?? 0)) + 1
      : 0;

    for (const { prefix, pageType, tabs } of tabsByConfig) {
      for (const tab of tabs) {
        const content = String(tab?.content || '');
        const embedded = parseContentCardsFromHtml(content);
        if (!embedded || embedded.cards.length === 0) continue;

        const targetPage = `${prefix}:${tab.id}` as ContentCardsTargetPage;
        try {
          await persistBlockModule(embedded, {
            targetPage,
            insertAfter: 'content-end',
            displayOrder: nextOrderBase++,
            isActive: true
          });
          await saveAboutTab({
            id: tab.id,
            configId,
            companyId,
            pageType,
            name: tab.name,
            title: tab.title,
            content: stripContentCardsFromHtml(content),
            backgroundColor: tab.backgroundColor,
            displayOrder: tab.displayOrder,
            isActive: tab.isActive,
            sections: tab.sections,
            parentId: tab.parentId,
            level: tab.level
          });
          migrated = true;
        } catch (error) {
          console.error('[CONTENT CARDS] Gömülü kart bloğu taşınamadı:', targetPage, error);
        }
      }
    }

    for (const page of customPages) {
      const content = String(page?.content || '');
      const embedded = parseContentCardsFromHtml(content);
      if (!embedded || embedded.cards.length === 0) continue;

      const targetPage = `custom-page:${page.id}` as ContentCardsTargetPage;
      try {
        await persistBlockModule(embedded, {
          targetPage,
          insertAfter: 'content-end',
          displayOrder: nextOrderBase++,
          isActive: true
        });
        await saveCustomPage({
          id: page.id,
          configId,
          title: page.title,
          slug: page.slug,
          content: stripContentCardsFromHtml(content),
          metaTitle: page.metaTitle,
          metaDescription: page.metaDescription,
          isActive: page.isActive,
          showInMenu: page.showInMenu,
          showInFooter: page.showInFooter,
          displayOrder: page.displayOrder
        });
        migrated = true;
      } catch (error) {
        console.error('[CONTENT CARDS] Gömülü kart bloğu taşınamadı:', targetPage, error);
      }
    }

    return migrated;
  };

  const loadAll = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      // Varsayılan ana bölümler her zaman listelenmeli (tab olmasa bile).
      let tabsByConfig: Array<{ prefix: string; pageType: string; group: string; tabs: any[] }> =
        TAB_TARGET_CONFIGS.map((cfg) => ({ ...cfg, tabs: [] as any[] }));
      let customPages: any[] = [];

      if (configId) {
        const tabResponses = await Promise.all(
          TAB_TARGET_CONFIGS.map((cfg) =>
            getAboutTabs(configId, cfg.pageType)
              .then((res: any) => (Array.isArray(res?.data) ? res.data : []))
              .catch(() => [])
          )
        );
        tabsByConfig = TAB_TARGET_CONFIGS.map((cfg, index) => ({ ...cfg, tabs: tabResponses[index] }));

        customPages = await getCustomPages(configId)
          .then((res: any) => (Array.isArray(res?.data) ? res.data : []))
          .catch(() => []);
      }

      const options: ContentCardsPageOption[] = [
        HOMEPAGE_OPTION,
        // Her ana bölüm için önce bölümün kendi (ana) sayfası, sonra oluşturulan alt sekmeleri gelir.
        ...tabsByConfig.flatMap(({ tabs, prefix, group }) => [
          {
            value: `${prefix}:main` as ContentCardsTargetPage,
            label: `${group} sayfası`,
            group
          },
          ...buildTabOptions(tabs, prefix, group)
        ]),
        ...customPages
          .filter((page: any) => page?.isActive !== false && page?.IsActive !== false)
          .map((page: any) => ({
            value: `custom-page:${page.id}` as ContentCardsTargetPage,
            label: String(page.title || page.slug || 'Özel Sayfa'),
            group: 'Özel Sayfalar'
          }))
      ];
      setPageTargetOptions(options);

      let blocks = await fetchModuleBlocks();

      if (configId && !legacyMigrationDoneRef.current) {
        legacyMigrationDoneRef.current = true;
        const migrated = await migrateLegacyEmbeddedBlocks(tabsByConfig, customPages, blocks);
        if (migrated) {
          blocks = await fetchModuleBlocks();
        }
      }

      setStoredBlocks(blocks);
      setEnabled(blocks.some((block) => block.enabled));

      const tagModulesResponse = await getProductModules(companyId, 'content-tags').catch(() => null);
      const tagRows = Array.isArray(tagModulesResponse?.data) ? tagModulesResponse.data : [];
      const tagSiblings: SiblingPageBlock[] = tagRows
        .map((row: any) => {
          try {
            const parsed = JSON.parse(row?.settingsJson || '{}');
            if (parsed?.enabled === false || row?.isActive === false) return null;
            const tags = Array.isArray(parsed?.tags) ? parsed.tags : [];
            if (tags.length === 0) return null;
            return {
              moduleId: String(row.id),
              targetPage: String(parsed?.position?.targetPage || 'homepage'),
              title: String(parsed?.title || '').trim() || `Etiket bloğu (${tags.length} etiket)`,
              kind: 'tags' as const
            };
          } catch {
            return null;
          }
        })
        .filter(Boolean) as SiblingPageBlock[];
      setSiblingTagBlocks(tagSiblings);
    } catch (error) {
      console.error('[CONTENT CARDS] Yükleme hatası:', error);
      setSnack({ open: true, type: 'error', message: 'İçerik kartları yüklenemedi.' });
    } finally {
      setLoading(false);
    }
  }, [companyId, configId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const buildCardsFromTemplate = (variant: ContentCardVariant) => {
    const template = getContentCardTemplate(variant);
    return template.sampleCards.map((card) =>
      normalizeContentCardForEditor({
        ...DEFAULT_CONTENT_CARDS_DIALOG.cards[0],
        ...card,
        bulletItems: Array.isArray(card.bulletItems) ? [...card.bulletItems] : []
      })
    );
  };

  const openEditor = (moduleId: string | null = null) => {
    setEditingModuleId(moduleId);

    if (moduleId) {
      const block = storedBlocks.find((item) => item.moduleId === moduleId);
      if (!block) return;

      const base = cleanContentCardsPayload(block.payload);
      const activeVariant = normalizeContentCardVariant(base.settings.variant);
      setSelectedTemplate(activeVariant);
      setDialogState({
        open: true,
        isEdit: true,
        title: base.title || '',
        settings: base.settings,
        cards: base.cards.length > 0
          ? base.cards.map(normalizeContentCardForEditor)
          : buildCardsFromTemplate(activeVariant),
        insertAfter: base.position?.insertAfter || (block.targetPage === 'homepage' ? 'categories' : 'content-end'),
        targetPage: block.targetPage
      });
      return;
    }

    const template = getContentCardTemplate(isPromoMode ? 'banner' : selectedTemplate);
    const promoCards = isPromoMode
      ? (template.sampleCards.length >= 3
          ? template.sampleCards.slice(0, 3).map((sample, i) => ({
              ...DEFAULT_CONTENT_CARD,
              ...sample,
              title: '',
              description: '',
              date: '',
              imageUrl: '',
              panelColor: sample.panelColor || BANNER_PANEL_COLORS[i % BANNER_PANEL_COLORS.length]
            }))
          : [0, 1, 2].map((i) => ({
              ...DEFAULT_CONTENT_CARD,
              panelColor: BANNER_PANEL_COLORS[i % BANNER_PANEL_COLORS.length],
              align: 'left' as const
            })))
      : [{ ...DEFAULT_CONTENT_CARD }];

    setDialogState({
      open: true,
      isEdit: false,
      title: isPromoMode ? 'Kampanyalar' : '',
      settings: {
        ...template.settings,
        variant: isPromoMode ? 'banner' : template.settings.variant,
        layoutTemplate: isPromoMode ? 'promo-trio' : template.settings.layoutTemplate,
        cardRadiusPx: isPromoMode ? 18 : template.settings.cardRadiusPx,
        showBullets: isPromoMode ? false : template.settings.showBullets,
        showDate: isPromoMode ? true : template.settings.showDate
      },
      cards: promoCards,
      insertAfter: 'categories',
      targetPage: 'homepage'
    });
  };

  const handleTargetPageChange = (targetPage: ContentCardsTargetPage) => {
    setDialogState((prev) => {
      const wasHomepage = (prev.targetPage || 'homepage') === 'homepage';
      const isHomepage = targetPage === 'homepage';
      if (wasHomepage && isHomepage) return { ...prev, targetPage };
      if (!wasHomepage && !isHomepage) {
        return { ...prev, targetPage, insertAfter: 'content-end' };
      }
      return {
        ...prev,
        targetPage,
        insertAfter: isHomepage ? 'categories' : 'content-end'
      };
    });
  };

  const resolveTargetLabel = (targetPage: ContentCardsTargetPage): string =>
    pageTargetOptions.find((option) => option.value === targetPage)?.label ||
    (targetPage === 'homepage' ? 'Ana Sayfa' : 'Silinmiş sayfa');

  const resolvePositionLabel = (block: ContentCardsBlockRecord): string => {
    const insertAfter = block.payload.position?.insertAfter || 'categories';
    if (block.targetPage !== 'homepage') {
      if (insertAfter === 'content-start') return 'İçeriğin üstünde';
      if (insertAfter === 'content-end') return 'İçeriğin altında';
      const cardSibling = storedBlocks.find((item) => item.moduleId === insertAfter);
      if (cardSibling) {
        const title =
          cardSibling.payload.title || `Kart yapısı (${cardSibling.payload.cards.length} kart)`;
        return `"${title}" kart yapısının altında`;
      }
      const tagSibling = siblingTagBlocks.find((item) => item.moduleId === insertAfter);
      if (tagSibling) return `"${tagSibling.title}" etiketlerinin altında`;
      return 'İçeriğin altında';
    }
    return positionOptions.find((option) => option.value === insertAfter)?.label || 'Kategorilerden sonra';
  };

  const handleSaveDialog = async () => {
    if (!companyId || !configId) {
      setSnack({ open: true, type: 'error', message: 'Önce e-ticaret yapılandırmasını kaydedin.' });
      return;
    }

    const targetPage = dialogState.targetPage || 'homepage';
    const isHomepage = targetPage === 'homepage';
    const selectedInsertAfter = String(dialogState.insertAfter || '').trim();
    const insertAfter: ContentCardsInsertAfter = isHomepage
      ? dialogState.insertAfter
      : selectedInsertAfter === 'content-start'
        ? 'content-start'
        : selectedInsertAfter && selectedInsertAfter !== 'content-end'
          ? (selectedInsertAfter as ContentCardsInsertAfter)
          : 'content-end';

    const payload = cleanContentCardsPayload({
      title: dialogState.title,
      cards: dialogState.cards,
      settings: dialogState.settings,
      position: { insertAfter, targetPage }
    });

    if (payload.cards.length === 0) {
      setSnack({
        open: true,
        type: 'error',
        message: 'En az bir kartın başlığını girmelisiniz. Diğer alanlar opsiyoneldir.'
      });
      return;
    }

    // Yalnızca başlık zorunlu; geri kalan tüm alanlar opsiyoneldir.
    const cardWithoutTitle = payload.cards.findIndex((card) => !String(card.title || '').trim());
    if (cardWithoutTitle >= 0) {
      setSnack({
        open: true,
        type: 'error',
        message: `Kart #${cardWithoutTitle + 1} için başlık zorunludur. Diğer alanlar opsiyoneldir.`
      });
      return;
    }

    const existingBlock = editingModuleId
      ? storedBlocks.find((block) => block.moduleId === editingModuleId)
      : null;
    const otherBlocks = editingModuleId
      ? storedBlocks.filter((block) => block.moduleId !== editingModuleId)
      : storedBlocks;

    let nextDisplayOrder: number;
    if (isHomepage) {
      const previousInsertAfter = existingBlock?.payload.position?.insertAfter || 'categories';
      const keepOrder =
        existingBlock && existingBlock.targetPage === 'homepage' && previousInsertAfter === insertAfter;
      nextDisplayOrder = keepOrder
        ? existingBlock.displayOrder
        : resolveNextHomepageOrderForAnchor(otherBlocks, insertAfter);
    } else {
      nextDisplayOrder =
        existingBlock && existingBlock.targetPage === targetPage
          ? existingBlock.displayOrder
          : resolveNextOrderForTarget(otherBlocks, targetPage);
    }

    setSaving(true);
    try {
      await persistBlockModule(payload, {
        moduleId: editingModuleId,
        targetPage,
        insertAfter,
        displayOrder: nextDisplayOrder,
        isActive: true
      });

      const isEditing = Boolean(editingModuleId);
      setDialogState(DEFAULT_CONTENT_CARDS_DIALOG);
      setEditingModuleId(null);
      await loadAll();
      setSnack({
        open: true,
        type: 'success',
        message: isEditing
          ? `Kart bloğu güncellendi. Sayfa: ${resolveTargetLabel(targetPage)}`
          : `Yeni kart bloğu eklendi. Sayfa: ${resolveTargetLabel(targetPage)}`
      });
    } catch (error) {
      console.error('[CONTENT CARDS] Kayıt hatası:', error);
      setSnack({ open: true, type: 'error', message: 'İçerik kartları kaydedilemedi.' });
    } finally {
      setSaving(false);
    }
  };

  const persistBlocksEnabled = async (blocks: ContentCardsBlockRecord[], checked: boolean) => {
    setSaving(true);
    try {
      for (const block of blocks) {
        const payload = cleanContentCardsPayload(block.payload);
        await persistBlockModule(payload, {
          moduleId: block.moduleId,
          targetPage: block.targetPage,
          insertAfter: block.payload.position?.insertAfter || (block.targetPage === 'homepage' ? 'categories' : 'content-end'),
          displayOrder: block.displayOrder,
          isActive: checked
        });
      }
      await loadAll();
      return true;
    } catch (error) {
      console.error('[CONTENT CARDS] Görünürlük güncelleme hatası:', error);
      setSnack({ open: true, type: 'error', message: 'Kart görünürlüğü güncellenemedi.' });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleEnabledChange = async (checked: boolean) => {
    if (storedBlocks.length === 0 && checked) {
      openEditor(null);
      return;
    }

    if (storedBlocks.length === 0) {
      setEnabled(false);
      return;
    }

    const ok = await persistBlocksEnabled(storedBlocks, checked);
    if (ok) {
      setEnabled(checked);
      setSnack({
        open: true,
        type: 'success',
        message: checked
          ? 'Tüm kart blokları sitede gösterilecek.'
          : 'Tüm kart blokları gizlendi. Bloklar silinmedi; istediğinizde tekrar açabilirsiniz.'
      });
    }
  };

  const handleToggleBlock = async (block: ContentCardsBlockRecord) => {
    const ok = await persistBlocksEnabled([block], !block.enabled);
    if (ok) {
      setSnack({
        open: true,
        type: 'success',
        message: block.enabled ? 'Kart bloğu gizlendi.' : 'Kart bloğu sitede gösterilecek.'
      });
    }
  };

  const handleDeleteBlock = async (moduleId: string) => {
    if (!companyId) return;

    setSaving(true);
    try {
      await deleteProductModule({ id: moduleId, companyId });
      await loadAll();
      setSnack({ open: true, type: 'success', message: 'Kart bloğu silindi.' });
    } catch (error) {
      console.error('[CONTENT CARDS] Silme hatası:', error);
      setSnack({ open: true, type: 'error', message: 'Kart bloğu silinemedi.' });
    } finally {
      setSaving(false);
    }
  };

  const groupedBlocks = useMemo(() => {
    const groups = new Map<string, ContentCardsBlockRecord[]>();
    storedBlocks.forEach((block) => {
      const label = resolveTargetLabel(block.targetPage);
      const list = groups.get(label) || [];
      list.push(block);
      groups.set(label, list);
    });

    return Array.from(groups.entries()).sort(([a], [b]) => {
      if (a === 'Ana Sayfa') return -1;
      if (b === 'Ana Sayfa') return 1;
      return a.localeCompare(b, 'tr');
    });
  }, [storedBlocks, pageTargetOptions, positionOptions]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          İçerik kartları yükleniyor...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
        {!isPromoMode && (
          <>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Kart Stil Şablonu
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Kartların görsel stilini seçin. Yerleşim (sol panel + ızgara vb.) blok eklerken / düzenlerken
          seçilir; kartları istediğiniz gibi sütunlara dizebilirsiniz.
        </Typography>
        <ContentCardsStylePreview
          value={selectedTemplate}
          onChange={(variant) => {
            setSelectedTemplate(variant);
            if (dialogState.open) {
              const template = getContentCardTemplate(variant);
              // Sadece görsel stil (settings) değişir; kullanıcının girdiği kartlar
              // korunur. Önceden kartların üzerine örnek kartlar yazılıyordu.
              setDialogState((prev) => ({
                ...prev,
                settings: { ...template.settings }
              }));
            }
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
          Seçili şablon: <strong>{getContentCardTemplateLabel(selectedTemplate)}</strong>
        </Typography>
          </>
        )}
        {isPromoMode && (
          <>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Sade Kampanya Banner
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sol görsel, sağ renkli panel. Her karta görsel, başlık, tarih ve CTA yazmanız yeterli.
            </Typography>
          </>
        )}
      </Box>

      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            onChange={(e) => handleEnabledChange(e.target.checked)}
            disabled={saving}
          />
        }
        label={isPromoMode ? 'Kampanya kartlarını sitede göster' : 'İçerik kartlarını sitede göster'}
      />

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
        {isPromoMode
          ? '“Kart Bloğu Oluştur” → 3 kart için görsel / başlık / renk seç → Kaydet.'
          : 'Kartları ana sayfaya, Hakkımızda / Hizmetler / Ürün ve Çözümler / Referanslar sekmelerine ve özel sayfalara ekleyebilirsiniz. Düğmeyi kapattığınızda bloklar silinmez, yalnızca sitede gizlenir.'}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
        <Button
          variant="contained"
          startIcon={storedBlocks.length > 0 ? <AddIcon /> : <ViewCarouselOutlinedIcon />}
          onClick={() => openEditor(null)}
          disabled={saving}
        >
          {storedBlocks.length > 0
            ? (isPromoMode ? 'Yeni Promo Kart Bloğu' : 'Yeni Kart Bloğu Ekle')
            : (isPromoMode ? 'Promo Kart Bloğu Oluştur' : 'Kart Bloğu Oluştur')}
        </Button>
      </Box>

      {groupedBlocks.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {groupedBlocks.map(([groupLabel, blocks]) => (
            <Box key={groupLabel}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                {groupLabel}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {blocks.map((block, index) => {
                  const variantLabel = getContentCardTemplateLabel(block.payload.settings.variant);
                  const layoutLabel = getContentCardLayoutLabel(
                    normalizeContentCardsLayout(block.payload.settings.layoutTemplate)
                  );
                  return (
                    <Alert
                      key={block.moduleId}
                      severity={block.enabled ? 'info' : 'warning'}
                      icon={block.enabled ? undefined : <VisibilityOffOutlinedIcon fontSize="inherit" />}
                      sx={{ alignItems: 'center' }}
                      action={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip title={block.enabled ? 'Bloğu gizle' : 'Bloğu göster'}>
                            <Switch
                              size="small"
                              checked={block.enabled}
                              onChange={() => handleToggleBlock(block)}
                              disabled={saving}
                            />
                          </Tooltip>
                          <IconButton size="small" onClick={() => openEditor(block.moduleId)} disabled={saving}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteBlock(block.moduleId)}
                            disabled={saving}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      }
                    >
                      <Typography variant="body2" component="span">
                        <strong>Blok {index + 1}:</strong> {block.payload.cards.length} kart
                        {block.payload.title ? ` · ${block.payload.title}` : ''}
                        {` · Stil: ${variantLabel}`}
                        {` · Yerleşim: ${layoutLabel}`}
                        {` · Konum: ${resolvePositionLabel(block)}`}
                      </Typography>
                      {!block.enabled && (
                        <Chip label="Gizli" size="small" color="warning" sx={{ ml: 1, height: 20 }} />
                      )}
                    </Alert>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <ContentCardsDialog
        state={dialogState}
        onClose={() => {
          setDialogState(DEFAULT_CONTENT_CARDS_DIALOG);
          setEditingModuleId(null);
        }}
        onSave={handleSaveDialog}
        onStateChange={(updater) => setDialogState((prev) => updater(prev))}
        onOpenImageLibrary={(cardIndex, field = 'imageUrl') =>
          setImageLibraryPicker({ open: true, targetIndex: cardIndex, field })
        }
        onTargetPageChange={handleTargetPageChange}
        pageTargetOptions={pageTargetOptions}
        positionOptions={positionOptions}
        pagePositionOptions={pagePositionOptions}
        simpleMode={isPromoMode}
      />

      <ImageLibrarySelectionModal
        open={imageLibraryPicker.open}
        onClose={() => setImageLibraryPicker({ open: false, targetIndex: -1, field: 'imageUrl' })}
        onSave={(selectedUrls) => {
          const selectedUrl = selectedUrls?.[0] || '';
          if (!selectedUrl || imageLibraryPicker.targetIndex < 0) {
            setImageLibraryPicker({ open: false, targetIndex: -1, field: 'imageUrl' });
            return;
          }
          const field = imageLibraryPicker.field || 'imageUrl';
          setDialogState((prev) => ({
            ...prev,
            cards: prev.cards.map((card, idx) =>
              idx === imageLibraryPicker.targetIndex ? { ...card, [field]: selectedUrl } : card
            )
          }));
          setImageLibraryPicker({ open: false, targetIndex: -1, field: 'imageUrl' });
        }}
        companyId={companyId}
        productName="İçerik Kartları"
        currentImageUrls={[]}
        productId={null}
        singleSelect
        maxSelection={1}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
          severity={snack.type}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HomePageContentCardsPanel;
